import { Box, Card, CardContent, Divider, Typography } from "@material-ui/core";
import IconMessage from "@material-ui/icons/MessageOutlined";
import {
  AutocompleteInput,
  BooleanInput,
  Button,
  FormDataConsumer,
  FormTab,
  FunctionField,
  ImageField,
  ImageInput,
  NumberInput,
  ReferenceInput,
  SelectInput,
  TabbedForm,
  TextInput,
  maxLength,
  maxValue,
  minLength,
  minValue,
  required,
} from "react-admin";
import { useFormState } from "react-final-form";
import { imageFieldFormatter } from "../common/image-field-formatter";
import { DesanaTabbedFormTabs } from "../components/DesanaTabbedFormTabs";
import SaveOnlyToolbar from "../components/SaveOnlyToolbar";
import Pricing from "./Pricing";
import { ResourcePropertiesInput } from "./ResourcePropertiesInput";
import { BookingLength } from "@desanaio/public-hops-grpc-web/dist/desana/type/v1/resource_pb";
import { enumToSelectionOptions } from "../common/enum-to-selection-options";

const validateName = [required(), minLength(3), maxLength(40)];
const validateDescription = [minLength(10), maxLength(1000)];
const validateSpace = [required()];
const validateCapacity = [required(), minValue(1)];
const validateAdvancedAvailability = [required(), minValue(7), maxValue(365)];

const ResourceEditForm = ({ classes, redirect, record, readOnly, ...props }) => {
  return (
    <TabbedForm
      mutationMode="pessimistic"
      tabs={<DesanaTabbedFormTabs hasAside={false} />}
      redirect={redirect}
      toolbar={readOnly ? null : <SaveOnlyToolbar />}
      record={record}
      {...props}
    >
      <FormTab label="Basic Details">
        <TextInput
          source="name"
          autoComplete="space-name"
          autoCorrect="off"
          spellCheck="off"
          validate={validateName}
          disabled={readOnly}
        />
        <TextInput
          source="description"
          multiline
          fullWidth
          validate={validateDescription}
          disabled={readOnly}
        />
        <TextInput
          multiline
          fullWidth
          source="autoresponder"
          helperText="Autoresponse message when workers book"
          disabled={readOnly}
        />
        <ReferenceInput
          label="Space"
          source="space.id"
          reference="spaces"
          perPage={100}
          sort={{ field: "name", order: "ASC" }}
        >
          <AutocompleteInput validate={validateSpace} disabled />
        </ReferenceInput>
      </FormTab>
      <FormTab label="Booking Info">
        <BooleanInput
          source="instantBook"
          helperText={`Can this ${(
            record?.type?.name || "resource"
          ).toLowerCase()} be booked without approval?`}
          disabled
        />

        <Divider fullWidth style={{ marginBottom: "1em" }} />
        <Typography variant="h6">Capacity</Typography>
        <NumberInput
          source="capacityRange.hi"
          label="Maximum"
          helperText="Maxiumum amount of people can this resource accommodate?"
          validate={validateCapacity}
          disabled
        />
        <NumberInput
          source="capacityRange.lo"
          label="Minimum"
          helperText="Minimum amount of people can this resource accommodate?"
          validate={validateCapacity}
          defaultValue={1}
          disabled
        />
        <Divider fullWidth style={{ marginBottom: "1em" }} />
        <Typography variant="h6">Availability</Typography>
        <NumberInput
          source="advancedAvailability"
          helperText={`How many days in advance can this ${(
            record?.type?.name || "resource"
          ).toLowerCase()} be booked?`}
          step={1}
          defaultValue={30}
          validate={validateAdvancedAvailability}
          disabled
        />
        <NumberInput
          source="minimumAdvanceNotice"
          helperText={`How many minutes notice before this ${(
            record?.type?.name || "resource"
          ).toLowerCase()} can be booked?`}
          inputProps={{
            step: 15,
            min: 0,
            max: 60,
          }}
          defaultValue={1440}
          disabled
        />
        {/* <MinimumBookingLengthSelector /> */}
        <Divider fullWidth />
        <ChangeAvailability />
      </FormTab>
      <FormTab label="Pricing">
        <Pricing />
      </FormTab>
      <FormTab label="Properties">
        <ResourcePropertiesInput disabled={readOnly} />
      </FormTab>
      <FormTab label="Images">
        <FormDataConsumer>
          {({ formData }) => {
            if (readOnly) {
              return (
                <>
                  {Array.isArray(formData.imagesList) &&
                  formData.imagesList.length > 0 ? (
                    formData.imagesList.map((image, idx) => {
                      const optimisedRecord = imageFieldFormatter(image);
                      return (
                        <ImageField
                          key={idx}
                          record={optimisedRecord}
                          source="url"
                          title=""
                        />
                      );
                    })
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No images available.
                    </Typography>
                  )}
                </>
              );
            }

            if (isHotDesk(formData.resourceType)) {
              return (
                <>
                  <Typography variant="body1" color="primary">
                    Due to the type of this resource, images are managed by the
                    space and cannot be edited here. To change the images update
                    the space.
                  </Typography>
                  {Array.isArray(formData.imagesList) &&
                  formData.imagesList.length > 0 ? (
                    formData.imagesList.map((image, idx) => {
                      const optimisedRecord = imageFieldFormatter(image);
                      return (
                        <ImageField
                          key={idx}
                          record={optimisedRecord}
                          source="url"
                          title=""
                        />
                      );
                    })
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No images available.
                    </Typography>
                  )}
                </>
              );
            }

            return (
              <ImageInput
                source="imagesList"
                label="Resource images"
                accept="image/*"
                multiple
              >
                <FunctionField
                  render={(record) => {
                    if (record.rawFile) {
                      return <ImageField record={record} />;
                    }
                    const optimisedRecord = imageFieldFormatter(record);
                    return (
                      <ImageField
                        record={optimisedRecord}
                        source="url"
                        title=""
                      />
                    );
                  }}
                />
              </ImageInput>
            );
          }}
        </FormDataConsumer>
      </FormTab>
    </TabbedForm>
  );
};

const ChangeAvailability = () => {
  const { values } = useFormState();

  if (!values?.name || !values?.id) {
    return null;
  }

  return (
    <Box component="span" m={2}>
      <Card mt={2}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Want to change your details?
          </Typography>
          <Typography variant="body2" gutterBottom>
            If you'd like to change any of these details, please get in touch
            with us below.
          </Typography>

          <Button
            label="Change details"
            alignIcon="right"
            color="primary"
            variant="outlined"
            onClick={() =>
              window.Intercom(
                "showNewMessage",
                `I'd like to change the details for "${values.name}" (${values.id}) to:\n\n[ENTER CHANGES HERE]`,
              )
            }
            style={{ marginTop: "20px" }}
          >
            <IconMessage />
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

const BookingLengthSelections = enumToSelectionOptions(
  BookingLength,
  "BOOKING_LENGTH_",
  false,
);

/**
 * The minimmum booking length selector.
 *
 * This isn't currently used in the UI, but is here for future use.
 * https://app.asana.com/1/816262025775307/project/1201021827386812/task/1209960015465510?focus=true
 */
const MinimumBookingLengthSelector = (props) => {
  const { values } = useFormState();

  const minimumBookingLength = values?.minimumBookingLength;

  const inputs = [
    <Typography variant="h6" style={{ fontSize: "1rem" }}>
      Minimum Booking Length
    </Typography>,
    <SelectInput
      {...props}
      source="minimumBookingLength.enabled"
      label="Minimum booking length type"
      helperText="The type of minimum booking length"
      choices={BookingLengthSelections}
      defaultValue={BookingLength.BOOKING_LENGTH_UNSPECIFIED}
      disabled
    />,
  ];

  if (minimumBookingLength?.enabled === BookingLength.BOOKING_LENGTH_MINUTES) {
    inputs.push(
      <NumberInput
        {...props}
        label="Duration"
        source="minimumBookingLength.minutes"
        helperText="The minimum booking length in minutes"
        disabled
      />,
    );
  }

  return <div className={props.className}>{inputs}</div>;
};

/**
 * Returns true if the given resourceType is a hot desk or internal hot desk.
 * @param {object} resourceType - The resource type object, expected to have an 'id' property.
 * @returns {boolean}
 */
function isHotDesk(resourceType) {
  if (!resourceType || !resourceType.id) return false;
  return (
    resourceType.id === import.meta.env.VITE_RESOURCE_TYPE_HOT_DESK ||
    resourceType.id === import.meta.env.VITE_RESOURCE_TYPE_HOT_DESK_INTERNAL
  );
}

export default ResourceEditForm;
